import { useState, useEffect, useCallback } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";

// Define types (adjust based on actual API response/request)
// Matches the backend schema added in schemas/document.py
// Export this interface
export interface DocumentBasicInfo {
  DocumentID: number;
  Name: string;
}

interface RefineParams {
  documentTypeId: number;
  generatedDocumentId: number;
  correct_file_content: string;
  sectionId?: number;
}

// Hook definition
export const usePromptRefinement = (
  workspace_id: string,
  documentTypeId?: number,
) => {
  const [availableDocuments, setAvailableDocuments] = useState<
    DocumentBasicInfo[]
  >([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const [isRefining, setIsRefining] = useState<boolean>(false);
  const [refinementError, setRefinementError] = useState<string | null>(null);
  const [refinementSuccess, setRefinementSuccess] = useState<boolean>(false);

  // Add state for refined data
  const [refinedSectionId, setRefinedSectionId] = useState<number | null>(null);
  const [refinedPromptText, setRefinedPromptText] = useState<string | null>(
    null,
  );

  // Replace useQuery with useEffect for fetching documents
  /*
  const { data: documents, isLoading: isLoadingDocuments, error: documentsError }: UseQueryResult<DocumentBasicInfo[], Error> = useQuery({
     queryKey: ['documentsList', documentTypeId], // Use documentTypeId in the key
     queryFn: async () => {
        if (!documentTypeId) return []; // Don't fetch if no ID
        // Use axiosInstance and corrected API path from plan 1.4
        const response = await axiosInstance.get<DocumentBasicInfo[]>(`/documents/by_type/${documentTypeId}/list`);
        return response.data;
     },
     enabled: !!documentTypeId, // Only run query if documentTypeId is provided
     staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  */

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!documentTypeId || !workspace_id) {
        setAvailableDocuments([]);
        setIsLoadingDocuments(false);
        setDocumentsError(null);
        return;
      }
      setIsLoadingDocuments(true);
      setDocumentsError(null); // Clear previous errors
      try {
        const response = await authFetch(
          `/api/workspace/meridian/documents/by_type/${documentTypeId}/list?workspace_id=${workspace_id}`,
          "fetch documents list",
        );
        setAvailableDocuments(response);
      } catch (err: any) {
        console.error("Error fetching documents list:", err);
        setDocumentsError(err.message || "Failed to load documents.");
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
  }, [documentTypeId, workspace_id]);

  // Replace useMutation with useCallback for refinement action
  /*
  const mutation = useMutation<void, Error, RefineParams>({ // <ResponseType, ErrorType, VariablesType>
    mutationFn: async ({ documentTypeId, generatedDocumentId, correctFile, sectionId }: RefineParams) => {
      // ... FormData creation ...
      await axiosInstance.post(`/admin/document_types/${documentTypeId}/refine_prompts`, formData, {
         headers: {
            'Content-Type': 'multipart/form-data',
         },
      });
    },
     onSuccess: () => {
         setError(null);
         if (documentTypeId) {
            queryClient.invalidateQueries({ queryKey: ['documentType', documentTypeId] });
         }
     },
     onError: (err: Error) => {
          if (axios.isAxiosError(err) && err.response) {
              setError(err.response.data?.detail || 'Failed to refine prompts.');
          } else if (err instanceof Error) {
              setError(err.message || 'An unexpected error occurred.');
          } else {
             setError('An unexpected error occurred.');
          }
     }
  });

  const refinePrompts = useCallback((params: RefineParams) => {
    setError(null); // Clear previous errors before new mutation
    return mutation.mutate(params);
  }, [mutation]);
  */

  const refinePrompts = useCallback(
    async (params: RefineParams) => {
      setIsRefining(true);
      setRefinementError(null);
      setRefinementSuccess(false);
      setRefinedSectionId(null); // Clear previous results
      setRefinedPromptText(null); // Clear previous results

      const formData = new FormData();
      formData.append(
        "generated_document_id",
        params.generatedDocumentId.toString(),
      );
      formData.append("correct_file_content", params.correct_file_content);
      if (params.sectionId !== undefined) {
        formData.append("section_id", params.sectionId.toString());
      }

      try {
        const response = await authFetch(
          `/api/workspace/meridian/admin/document_types/${params.documentTypeId}/refine_prompts?workspace_id=${workspace_id}`,
          "refine prompts",
          {
            method: "POST",
            body: formData,
          },
        );

        setRefinementSuccess(true);
        // Store refined data if present in response, using likely backend keys
        const refinedDataFromApi = response.refined_data;
        if (refinedDataFromApi) {
          // Use potential snake_case keys from backend service response
          const sectionId =
            refinedDataFromApi.refinedSectionId ||
            refinedDataFromApi.section_id;
          const promptText =
            refinedDataFromApi.refinedPromptText ||
            refinedDataFromApi.refined_prompt;

          if (sectionId !== undefined && promptText !== undefined) {
            setRefinedSectionId(sectionId);
            setRefinedPromptText(promptText);
          }
        }
      } catch (err: any) {
        console.error("Error refining prompts:", err);
        setRefinementError(err.message || "Failed to refine prompts.");
      } finally {
        setIsRefining(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
    [workspace_id],
  );

  return {
    // Document List for Selector
    availableDocuments, // Use state variable directly
    isLoadingDocuments,
    documentsError, // Use state variable directly

    // Refinement Action
    refinePrompts,
    isRefining, // Use state variable directly
    refinementError,
    refinementSuccess, // Use state variable directly

    // Add refined data state to return value
    refinedSectionId,
    refinedPromptText,
  };
};
